import { gridlineProps, type GridlineShape } from "sketch/shapes/gridline";
import {
  Edge2d,
  SVGContainer,
  ShapeUtil,
  Vec,
  type Geometry2d,
  type TLIndicatorPath,
} from "tldraw";

import { annotationScale } from "@/sketch/annotation-scale";

/**
 * A construction gridline: a dash-dot line with a label bubble. Position
 * rides the shape's x (axis "x", a vertical line) or y (axis "y"). Extents
 * adapt to the drawing: the line spans the content plus crossing gridlines
 * with a margin, like a drafter rules them. The bubble and lettering draw
 * at the sheet's annotation scale, matching the dims and callouts; hit
 * geometry is the line alone so bounds stay zoom-invariant (bound dim
 * anchors resolve against bounds and must not drift with the camera).
 */
const MARGIN = 72; // 6ft past the drawing
const MIN_HALF_LENGTH = 144; // a bare gridline is 24ft
const BUBBLE_RADIUS = 16; // screen px
const LABEL_PX = 14;

export class GridlineShapeUtil extends ShapeUtil<GridlineShape> {
  static override type = "gridline" as const;
  static override props = gridlineProps;

  override getDefaultProps(): GridlineShape["props"] {
    return { axis: "x", label: "A" };
  }

  override canResize() {
    return false;
  }

  /** Construction, not geometry: never a bounds-snap target (that would
   *  snap shapes off the line). Graph paper and dims land things on it. */
  override getBoundsSnapGeometry() {
    return { points: [] };
  }

  override hideRotateHandle() {
    return true;
  }

  override canEdit() {
    return false;
  }

  /**
   * The perpendicular span of everything the gridline serves, in the
   * shape's local axis. Content shapes contribute their bounds; crossing
   * gridlines contribute only their scalar position (never their geometry,
   * which would cycle); dims and callouts are annotation that follows the
   * drawing and are skipped for the same reason (a callout stands clear of
   * the dims, which measure to gridlines). Reading other shapes here is
   * deliberate: the geometry cache tracks them, so the line re-rules itself
   * as the drawing grows.
   */
  private extent(shape: GridlineShape): { lo: number; hi: number } {
    const isX = shape.props.axis === "x";
    let min = Infinity;
    let max = -Infinity;
    for (const other of this.editor.getCurrentPageShapes()) {
      if (other.id === shape.id) continue;
      if (other.type === "gridline") {
        const cross = other as GridlineShape;
        if (cross.props.axis === shape.props.axis) continue;
        const position = cross.props.axis === "x" ? cross.x : cross.y;
        min = Math.min(min, position);
        max = Math.max(max, position);
        continue;
      }
      if (other.type === "dim" || other.type === "callout") continue;
      const bounds = this.editor.getShapePageBounds(other.id);
      if (!bounds) continue;
      min = Math.min(min, isX ? bounds.minY : bounds.minX);
      max = Math.max(max, isX ? bounds.maxY : bounds.maxX);
    }
    const origin = isX ? shape.y : shape.x;
    if (min > max) return { lo: -MIN_HALF_LENGTH, hi: MIN_HALF_LENGTH };
    return { lo: min - MARGIN - origin, hi: max + MARGIN - origin };
  }

  /** Local space: the line runs through the shape origin along its axis. */
  private ends(shape: GridlineShape): [Vec, Vec] {
    const { lo, hi } = this.extent(shape);
    return shape.props.axis === "x"
      ? [new Vec(0, lo), new Vec(0, hi)]
      : [new Vec(lo, 0), new Vec(hi, 0)];
  }

  /** The bubble hangs just past the north end (x axis) or west end (y). */
  private bubbleCenter(shape: GridlineShape, k: number): Vec {
    const [start] = this.ends(shape);
    return shape.props.axis === "x"
      ? new Vec(start.x, start.y - BUBBLE_RADIUS * k)
      : new Vec(start.x - BUBBLE_RADIUS * k, start.y);
  }

  override getGeometry(shape: GridlineShape): Geometry2d {
    const [start, end] = this.ends(shape);
    return new Edge2d({ start, end });
  }

  /** Shared by the canvas component and SVG export (which has no CSS vars). */
  private svg(
    shape: GridlineShape,
    colors: { line: string; fill: string; text: string },
    k: number,
  ) {
    const [start, end] = this.ends(shape);
    const bubble = this.bubbleCenter(shape, k);
    return (
      <g>
        <line
          x1={start.x}
          y1={start.y}
          x2={end.x}
          y2={end.y}
          stroke={colors.line}
          strokeWidth={1 * k}
          strokeDasharray={`${24 * k} ${6 * k} ${4 * k} ${6 * k}`}
        />
        <circle
          cx={bubble.x}
          cy={bubble.y}
          r={BUBBLE_RADIUS * k}
          fill={colors.fill}
          stroke={colors.line}
          strokeWidth={1.25 * k}
        />
        <text
          x={bubble.x}
          y={bubble.y}
          textAnchor="middle"
          dominantBaseline="central"
          fontSize={LABEL_PX * k}
          fontFamily="var(--tl-font-draw)"
          fill={colors.text}
        >
          {shape.props.label}
        </text>
      </g>
    );
  }

  private annotationScale(): number {
    return annotationScale(this.editor);
  }

  override component(shape: GridlineShape) {
    return (
      <SVGContainer>
        {this.svg(
          shape,
          {
            line: "var(--tl-color-text-3)",
            fill: "var(--tl-color-background)",
            text: "var(--tl-color-text-1)",
          },
          this.annotationScale(),
        )}
      </SVGContainer>
    );
  }

  override toSvg(shape: GridlineShape) {
    // Exports bake the zoom at export time, matching the dims.
    return this.svg(
      shape,
      { line: "#8a8f98", fill: "#ffffff", text: "#1d1d1d" },
      this.annotationScale(),
    );
  }

  override getIndicatorPath(shape: GridlineShape): TLIndicatorPath {
    const [start, end] = this.ends(shape);
    const k = this.annotationScale();
    const bubble = this.bubbleCenter(shape, k);
    const path = new Path2D();
    path.moveTo(start.x, start.y);
    path.lineTo(end.x, end.y);
    path.moveTo(bubble.x + BUBBLE_RADIUS * k, bubble.y);
    path.arc(bubble.x, bubble.y, BUBBLE_RADIUS * k, 0, Math.PI * 2);
    return path;
  }
}
